import React, { useState, useEffect } from "react";
import MainLayouts from "../../Layout/MainLayouts";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ReactComponent as ArrowLeft } from "bootstrap-icons/icons/arrow-left.svg";
import Spinner from "../spinner/Spinner";
import { useBankAccountById, useCreateBankAccount, useUpdateBankAccount } from "../../hooks/useInventory";

const BankAccountForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const accountId = searchParams.get("id");
  const isEdit = !!accountId;

  const { data: existing, isLoading } = useBankAccountById(accountId);
  const createMutation = useCreateBankAccount();
  const updateMutation = useUpdateBankAccount();

  const [form, setForm] = useState({ bank_name: "", account_name: "", account_number: "", status: "active" });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (existing?.data) {
      const a = existing.data;
      setForm({ bank_name: a.bank_name || "", account_name: a.account_name || "", account_number: a.account_number || "", status: a.status || "active" });
    }
  }, [existing]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: "" }));
  };

  const validate = () => {
    const errs = {};
    if (!form.bank_name.trim()) errs.bank_name = "Bank name is required";
    if (!form.account_name.trim()) errs.account_name = "Account name is required";
    return errs;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    const mutation = isEdit ? updateMutation : createMutation;
    const payload = isEdit ? { ...form, bank_account_id: parseInt(accountId) } : form;
    mutation.mutate(payload, { onSuccess: () => navigate("/head-office/inventory/bank-accounts") });
  };

  const isPending = createMutation.isLoading || updateMutation.isLoading;
  if (isEdit && isLoading) return <MainLayouts><Spinner loading /></MainLayouts>;

  const inputClass = (name) =>
    `w-full border ${errors[name] ? "border-red-500" : "border-gray-300"} rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange1`;

  return (
    <MainLayouts>
      <div className="p-6">
        <div className="flex items-center gap-4 mb-6">
          <button onClick={() => navigate("/head-office/inventory/bank-accounts")} className="text-orange1 hover:text-orange-600">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-2xl font-bold">{isEdit ? "Edit Bank Account" : "Add Bank Account"}</h1>
        </div>

        <div className="bg-white rounded-lg shadow p-6 max-w-2xl">
          <form onSubmit={handleSubmit}>
            <fieldset disabled={isPending} className="disabled:opacity-60">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name <span className="text-red-500">*</span></label>
                  <input name="bank_name" value={form.bank_name} onChange={handleChange} className={inputClass("bank_name")} placeholder="e.g. First Bank" />
                  {errors.bank_name && <p className="text-red-500 text-xs mt-1">{errors.bank_name}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Account Name <span className="text-red-500">*</span></label>
                  <input name="account_name" value={form.account_name} onChange={handleChange} className={inputClass("account_name")} />
                  {errors.account_name && <p className="text-red-500 text-xs mt-1">{errors.account_name}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Account Number</label>
                  <input name="account_number" value={form.account_number} onChange={handleChange} className={inputClass("account_number")} placeholder="10-digit number" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select name="status" value={form.status} onChange={handleChange} className={inputClass("status")}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button type="button" onClick={() => navigate("/head-office/inventory/bank-accounts")} disabled={isPending} className="px-5 py-2 border border-gray-300 rounded hover:bg-gray-50 transition text-sm disabled:cursor-not-allowed">
                  Cancel
                </button>
                <button type="submit" disabled={isPending} className="px-5 py-2 bg-orange1 text-white rounded hover:bg-orange-600 transition text-sm disabled:opacity-50 disabled:cursor-not-allowed">
                  {isPending ? "Saving..." : isEdit ? "Update Account" : "Add Account"}
                </button>
              </div>
            </fieldset>
          </form>
        </div>
      </div>
    </MainLayouts>
  );
};

export default BankAccountForm;
